import {SyncStatusItem} from '../interfaces/core/sync-status-item';
import {Table} from 'dexie';
import {DateTime} from 'luxon';
import {SyncItem} from '../interfaces/core/sync-item';
import {db} from './db.service';
import {TableName} from '../enums/core/table-name';
import {SyncException} from '../exceptions/sync.exception';
import {Subject} from 'rxjs';
import {Injectable} from '@angular/core';

Injectable({
  providedIn: 'root'
})
export class SyncStatusItemsTable {

  private readonly _table: Table<SyncStatusItem, number>;
  private readonly _change$: Subject<void>;

  constructor() {
    this._table = db.syncStatusItems;
    this._change$ = new Subject<void>();
  }

  get change$(): Subject<void> {
    return this._change$;
  }

  /**
   *
   */
  public async getToSync(): Promise<SyncStatusItem[]> {
    return this._table.where('isSynced').equals(0).sortBy('id');
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Single item
  // -------------------------------------------------------------------------------------------------------------------
  public async getByPrimaryKey(primaryKey: string): Promise<SyncStatusItem> {
    return this._table.where('primaryKey').equals(primaryKey).first();
  }

  /**
   *
   * @param tableName
   * @param primaryKey
   * @param isSynced
   */
  public async create(tableName: TableName, primaryKey: string, isSynced: boolean = false): Promise<void> {
    await this._table.add(this.setBasicProperties(tableName, primaryKey, isSynced));
    this._change$.next();
  }

  /**
   *
   * @param primaryKey
   * @param isSynced
   */
  public async update(primaryKey: string, isSynced: boolean = false): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, async () => {
      let syncStatusItem = await this._table.where('primaryKey').equals(primaryKey).first();
      syncStatusItem = this.setUpdateProperties(syncStatusItem, isSynced);
      await this._table.update(syncStatusItem.id, syncStatusItem);
    });
    this._change$.next();
  }

  /**
   *
   * @param tableName
   * @param primaryKey
   * @param isSynced
   */
  public async addOrUpdate(tableName: TableName, primaryKey: string, isSynced: boolean = false): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, async () => {
      let syncStatusItem = await this._table.where('primaryKey').equals(primaryKey).first();
      if(syncStatusItem) {
        await this.update(primaryKey, isSynced);
      }
      else {
        await this.create(tableName, primaryKey, isSynced);
      }
    });
  }

  /**
   *
   * @param primaryKey
   * @param force
   */
  public async remove(primaryKey: string, force: boolean = false): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, async () => {
      const syncStatusItem = await this._table.where('primaryKey').equals(primaryKey).first();
      if(force || !!!syncStatusItem.lastSync) {
        await this._table.delete(syncStatusItem.id);
      }
      else {
        await this.setToDeleted(syncStatusItem.id);
      }
    });
    this._change$.next();
  }

  /**
   *
   * @param id
   */
  private async setToDeleted(id: number): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, async () => {
      let syncStatusItem = await this._table.where('id').equals(id).first();
      syncStatusItem = this.setUpdateProperties(syncStatusItem);
      syncStatusItem.deleted = 1;
      await this._table.update(syncStatusItem.id, syncStatusItem);
    });
  }

  /**
   *
   * @param tableName
   */
  public async removeByTableName(tableName: string): Promise<void> {
    await this._table.where('tableName').equals(tableName).delete();
    this._change$.next();
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Many items
  // -------------------------------------------------------------------------------------------------------------------

  /**
   *
   * @param primaryKeys
   */
  public async getAllByPrimaryKey(primaryKeys: string[]): Promise<SyncStatusItem[]> {
    return this._table.where('primaryKey').anyOf(primaryKeys).toArray();
  }

  /**
   *
   * @param tableName
   * @param primaryKeys
   * @param isSynced
   */
  public async createMany(tableName: TableName, primaryKeys: string[], isSynced: boolean = false): Promise<void> {
    const syncStatusItems: SyncStatusItem[] = [];
    for(const primaryKey of primaryKeys) {
      syncStatusItems.push(this.setBasicProperties(tableName, primaryKey, isSynced));
    }
    await this._table.bulkAdd(syncStatusItems);
    this._change$.next();
  }

  /**
   *
   * @param tableName
   * @param primaryKeys
   * @param isSynced
   */
  public async updateMany(tableName: TableName, primaryKeys: string[], isSynced: boolean = false): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, async () => {
      let syncStatusItems =
        await this._table
          .where('primaryKey')
          .anyOf(primaryKeys)
          .filter(syncStatusItem => syncStatusItem.tableName === tableName)
          .toArray();
      syncStatusItems = syncStatusItems.map(syncStatusItem => {
        return this.setUpdateProperties(syncStatusItem, isSynced);
      });
      await this._table.bulkPut(syncStatusItems);
    });
    this._change$.next();
  }

  /**
   *
   * @param tableName
   * @param primaryKeys
   * @param isSynced
   */
  public async addOrUpdateMany(tableName: TableName, primaryKeys: string[], isSynced: boolean = false): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, async () => {
      let syncStatusItems =
        await this._table
          .where('primaryKey')
          .anyOf(primaryKeys)
          .filter(syncStatusItem => syncStatusItem.tableName === tableName)
          .toArray();
      await this.updateMany(tableName, primaryKeys, isSynced);
      primaryKeys = primaryKeys.filter(_primaryKey => {
        return !syncStatusItems.some(_syncStatusItem => _primaryKey === _syncStatusItem.primaryKey);
      });
      await this.createMany(tableName, primaryKeys, isSynced);
    });
  }

  /**
   *
   * @param primaryKeys
   * @param force
   */
  public async removeMany(primaryKeys: string[], force: boolean = false): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, async () => {
      const syncStatusItems =
        await this._table
          .where('primaryKey')
          .anyOf(primaryKeys)
          .toArray();

      if(force) {
        await this._table.bulkDelete(syncStatusItems.map(syncStatusItem => syncStatusItem.id));
      }
      else {
        await this.setToDeletedMany(syncStatusItems.map(syncStatusItem => syncStatusItem.id));
      }
    });
    this._change$.next();
  }

  /**
   *
   * @param ids
   * @private
   */
  private async setToDeletedMany(ids: number[]): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, async () => {
      let syncStatusItems = await this._table.where('id').anyOf(ids).toArray();
      for(let syncStatusItem of syncStatusItems) {
        syncStatusItem = this.setUpdateProperties(syncStatusItem);
        syncStatusItem.deleted = 1;
      }
      await this._table.bulkPut(syncStatusItems);
    });
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------------------------------------------------

  /**
   *
   * @param tableName
   */
  public async getNotSynced(tableName: TableName = null): Promise<SyncStatusItem[]> {
    if(tableName) {
      return this._table.where({tableName: tableName, isSynced: 0}).toArray();
    }
    return this._table.where('isSynced').equals(0).toArray();
  }

  /**
   *
   * @param tableName
   */
  public async getSynced(tableName: TableName = null): Promise<SyncStatusItem[]> {
    if(tableName) {
      return this._table.where({tableName: tableName, isSynced: 1}).toArray();
    }
    return this._table.where('isSynced').equals(1).toArray();
  }

  /**
   *
   * @param tables
   */
  public async isSynced(tables: string[] = []): Promise<boolean> {
    if(tables.length > 0) {
      for(const table of tables) {
        const syncStatusItems =
          await this._table.where({tableName: table, isSynced: 0}).toArray();
        if(syncStatusItems.length > 0) {
          return false;
        }
      }
      return true;
    }
    else {
      const syncStatusItems =
        await this._table.where('isSynced').equals(0).toArray();
      return syncStatusItems.length <= 0;
    }
  }

  /**
   *
   * @param tableName
   * @param primaryKeys
   */
  public async isSyncedByPrimaryKeys(tableName: TableName, primaryKeys: string[]): Promise<boolean> {
    const syncStatusItems = await this._table.where({
      tableName: tableName,
      isSynced: 0
    }).filter(_syncStatusItem => {
      return primaryKeys.some(_primaryKey => _primaryKey === _syncStatusItem.primaryKey);
    }).toArray();
    return syncStatusItems.length <= 0;
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Functions
  // -------------------------------------------------------------------------------------------------------------------
  /**
   *
   * @param tableName
   * @param primaryKey
   * @param isSynced
   * @private
   */
  private setBasicProperties(tableName: TableName, primaryKey: string, isSynced: boolean = false): SyncStatusItem {
    return {
      id: undefined,
      lastSync: isSynced ? DateTime.local().toISO() : null,
      errorCode: null,
      errorMessage: null,
      isSyncing: 0,
      tableName: tableName,
      isSynced: isSynced ? 1 : 0,
      primaryKey: primaryKey,
      deleted: 0,
      hasConflict: 0
    };
  }
  // -------------------------------------------------------------------------------------------------------------------
  // Synchronisation
  // -------------------------------------------------------------------------------------------------------------------
  public async setSyncInProcess(syncStatusItemIds: number[]): Promise<void> {
    let syncStatusItems = await this._table.where('id').anyOf(syncStatusItemIds).toArray();
    syncStatusItems = syncStatusItems.map(syncStatusItem => {
      syncStatusItem.isSyncing = 1;
      return syncStatusItem;
    });
    await this._table.bulkPut(syncStatusItems);
    this._change$.next();
  }

  /**
   *
   * @param itemsToSync
   * @param tableName
   */
  public async setSyncSuccessfully(itemsToSync: SyncItem[], tableName: TableName): Promise<void> {
    const table = db.getSyncTableByTableName(tableName);
    return db.transaction('rw', table, db.syncStatusItems, async () => {
      const itemsFromTable = await table
        .where('uuid')
        .anyOf(itemsToSync.map(itemToSync => itemToSync.uuid))
        .toArray();

      const syncStatusItems = await this._table
        .where('primaryKey')
        .anyOf(itemsToSync.map(item => item.uuid))
        .toArray();

      for(const syncStatusItem of syncStatusItems) {
        syncStatusItem.lastSync = DateTime.local().toISO();
        syncStatusItem.errorCode = null;
        syncStatusItem.errorMessage = null;
        syncStatusItem.hasConflict = 0;
        syncStatusItem.isSyncing = 0;
        if(syncStatusItem.deleted === 1) {
          continue;
        }
        const itemFromTable = itemsFromTable.find(_itemFromTable => _itemFromTable.uuid === syncStatusItem.primaryKey);
        const itemToSync = itemsToSync.find(_itemToSync => _itemToSync.uuid === syncStatusItem.primaryKey);
        const modifiedItemSynced = DateTime.fromISO(itemFromTable.modified);
        const modifiedItemFromTable = DateTime.fromISO(itemToSync.modified);
        if(modifiedItemSynced.equals(modifiedItemFromTable)) {
          syncStatusItem.isSynced = 1;
        }
      }
      await this._table.bulkPut(syncStatusItems);
      this._change$.next();
    });
  }

  /**
   *
   * @param syncStatusItemIds
   * @param syncException
   */
  public async setSyncError(syncStatusItemIds: number[], syncException: SyncException): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, async () => {
      const syncStatusItems = await this._table.where('id').anyOf(syncStatusItemIds).toArray();
      for(const syncStatusItem of syncStatusItems) {
        syncStatusItem.errorCode = syncException.code;
        syncStatusItem.errorMessage = syncException.message;
        syncStatusItem.isSyncing = 0;
        if(syncException.code === 409) {
          syncStatusItem.hasConflict = 1;
        }
      }
      await this._table.bulkPut(syncStatusItems);
    });
    this._change$.next();
  }

  /**
   *
   * @param syncStatusItems
   */
  public async setSyncDeletedItems(syncStatusItems: SyncStatusItem[]): Promise<void> {
    await this._table.bulkDelete(syncStatusItems.map(_syncStatusItem => _syncStatusItem.id));
    this._change$.next();
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Functions
  // -------------------------------------------------------------------------------------------------------------------
  /**
   *
   * @param syncStatusItem
   * @param isSynced
   * @private
   */
  private setUpdateProperties(syncStatusItem: SyncStatusItem, isSynced: boolean = false): SyncStatusItem {
    syncStatusItem.isSynced = isSynced ? 1 : 0;
    if(isSynced) {
      syncStatusItem.lastSync = DateTime.local().toISO();
    }
    return syncStatusItem;
  }

}
